/**
 * The forms an urgent inspection produces, filled from its own record.
 *
 * Four, each for a different reader:
 *
 *   SNOWTAM (ICAO, Global Reporting Format) — the runway condition report as
 *     the aeronautical information service disseminates it: items A–H for
 *     each runway assessed, then the situational-awareness items that apply.
 *     Since the GRF (November 2021) the SNOWTAM carries water and sand as well
 *     as snow, so it is the form for a rainstorm as much as a snowfall.
 *   FICON NOTAM (FAA) — the same assessment as an FAA field-condition NOTAM,
 *     coverage in percent and depth in inches.
 *   Self-inspection record (FAA Part 139) — the special inspection after
 *     unusual weather, the discrepancies found and what was done about each.
 *   Inspection report — the whole inspection in plain language, for airport
 *     management or the civil aviation authority.
 *
 * Every one is a DRAFT in the published format, not the issued document. The
 * SNOWTAM and the NOTAM are issued through the AIS and the NOTAM system by the
 * people authorised to issue them; the aerodrome manual says who. The formats
 * here follow ICAO PANS-AIM / PANS-Aerodromes and FAA AC 150/5200-30 as
 * published; wording should be checked against the current editions before the
 * aerodrome relies on it, and the screen says so.
 *
 * A form is generated only from what was recorded. A runway whose three thirds
 * have not all been assessed is left out of the SNOWTAM and the NOTAM rather
 * than reported with a guess, and the form says which runway was left out.
 */
import {
  areasOf,
  CHECK_LABEL,
  CONTAMINANT,
  coverageBand,
  rcrLine,
  statesOf,
  THIRD_LABEL,
  TRIGGERS,
  VERDICT_LABEL,
  type AreaState,
  type Contaminant,
  type MovementArea,
  type UrgentInspection,
} from "./urgentInspection";

export type FormKey = "snowtam" | "ficon" | "part139" | "report";

export const FORMS: { key: FormKey; label: string; authority: string; what: string }[] = [
  {
    key: "snowtam",
    label: "SNOWTAM — runway condition report",
    authority: "ICAO",
    what: "The Global Reporting Format: RWYCC, coverage, depth and condition for each third, then taxiway, apron and sand remarks. For the AIS to issue.",
  },
  {
    key: "ficon",
    label: "FICON NOTAM — field condition",
    authority: "FAA",
    what: "The same assessment in the FAA field-condition NOTAM format: codes, percent coverage and depth in inches.",
  },
  {
    key: "part139",
    label: "Self-inspection record — special inspection",
    authority: "FAA Part 139",
    what: "The special inspection after unusual weather: every area, each discrepancy and the action taken, signed.",
  },
  {
    key: "report",
    label: "Urgent inspection report",
    authority: "Airport management / regulator",
    what: "The whole inspection in plain language: trigger, every area's result, hazards, work raised, the outcome and the log.",
  },
];

export type RecipientKey = "management" | "regulator" | "ais" | "ats";

export const RECIPIENTS: { key: RecipientKey; label: string }[] = [
  { key: "management", label: "Airport management" },
  { key: "regulator", label: "Civil aviation authority" },
  { key: "ais", label: "AIS / NOTAM office" },
  { key: "ats", label: "Air traffic services" },
];

export interface Submission {
  id: string;
  /** a reference the recipient can quote back, e.g. UI-OEMA-20260918-02 */
  ref: string;
  form: FormKey;
  to: RecipientKey;
  address?: string;
  at: string;
  by: string;
  /** exactly what was sent */
  content: string;
  /** true when the operator changed the generated text before sending */
  amended: boolean;
  /** SHA-256 of `content`, so a later change to the saved copy is detectable */
  sha256?: string;
}

export interface FormInput {
  insp: UrgentInspection;
  area: MovementArea;
  site: string;
  /** the time the form is produced, for its own header */
  now: Date;
  /** a SNOWTAM serial, where the aerodrome numbers them */
  serial?: number;
}

/* ── helpers ──────────────────────────────────────────────────────────── */

const p2 = (n: number) => String(n).padStart(2, "0");

/** MMDDhhmm in UTC — the SNOWTAM's date-time group. */
export function mmddhhmm(iso: string): string {
  const d = new Date(iso);
  return `${p2(d.getUTCMonth() + 1)}${p2(d.getUTCDate())}${p2(d.getUTCHours())}${p2(d.getUTCMinutes())}`;
}

/** YYMMDDhhmm in UTC — the FAA "observed at" group. */
export function yymmddhhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getUTCFullYear()).slice(2)}${mmddhhmm(iso)}`;
}

const utc = (iso: string) => `${iso.slice(0, 16).replace("T", " ")} UTC`;

/** Millimetres to the nearest eighth of an inch, as the FAA reports depth. */
export function inches(mm: number): string {
  const eighths = Math.max(1, Math.round((mm / 25.4) * 8));
  const whole = Math.floor(eighths / 8);
  let frac = eighths % 8;
  let den = 8;
  while (frac && frac % 2 === 0 && den > 1) {
    frac /= 2;
    den /= 2;
  }
  const f = frac ? `${frac}/${den}` : "";
  return `${whole ? whole : ""}${whole && f ? " " : ""}${f}IN`;
}

const FAA_TERM: Record<Contaminant, string> = {
  dry: "DRY",
  wet: "WET",
  standingWater: "WATER",
  slush: "SLUSH",
  drySnow: "DRY SN",
  wetSnow: "WET SN",
  compactedSnow: "COMPACTED SN",
  frost: "FROST",
  ice: "ICE",
  wetIce: "WET ICE",
  waterOnCompactedSnow: "WATER OVER COMPACTED SN",
  snowOnCompactedSnow: "DRY SN OVER COMPACTED SN",
  snowOnIce: "DRY SN OVER ICE",
};

function where(s: AreaState): string {
  return s.area.third ? `${s.area.name} ${THIRD_LABEL[s.area.third].toLowerCase()}` : s.area.name;
}

function findingsOf(s: AreaState): string[] {
  const c = s.latest;
  if (!c) return [];
  return [...s.hazards, ...s.cleared].map(
    (k) => `${CHECK_LABEL[k]}${c.notes?.[k] ? ` — ${c.notes[k]}` : ""} (${s.hazards.includes(k) ? "OPEN" : "cleared"})`,
  );
}

/** The runways whose three thirds are all assessed, and those that are not. */
function runwaysReady(states: AreaState[], m: MovementArea) {
  const ready = m.runways.filter((r) => rcrLine(states, r) != null);
  const missing = m.runways.filter((r) => rcrLine(states, r) == null);
  return { ready, missing };
}

function thirdsOf(states: AreaState[], zone: string) {
  return ([1, 2, 3] as const).map((n) => states.find((s) => s.area.zone === zone && s.area.third === n)!);
}

/** When a runway was assessed: the latest of its three thirds. */
function assessedAt(states: AreaState[], zone: string): string {
  return thirdsOf(states, zone)
    .map((s) => s.latest!.at)
    .sort()
    .at(-1)!;
}

/* ── ICAO SNOWTAM ─────────────────────────────────────────────────────── */

export function snowtam(f: FormInput): string {
  const areas = areasOf(f.area);
  const states = statesOf(f.insp, areas);
  const { ready, missing } = runwaysReady(states, f.area);
  const L: string[] = [];
  L.push(`SNOWTAM ${String(f.serial ?? 1).padStart(4, "0")}`);
  L.push("DRAFT — issued by the AIS; abbreviated heading and addressing added on issue");
  L.push("");
  L.push("AEROPLANE PERFORMANCE CALCULATION SECTION");
  L.push(`A) ${f.site}`);
  if (!ready.length) L.push("   NO RUNWAY FULLY ASSESSED — NOTHING TO REPORT YET");
  for (const r of ready) {
    const th = thirdsOf(states, r.zone);
    const line = rcrLine(states, r)!.split(" ");
    L.push(`B) ${mmddhhmm(assessedAt(states, r.zone))}`);
    L.push(`C) ${r.from}`);
    L.push(`D) ${line[2]}`);
    L.push(`E) ${line[3]}`);
    L.push(`F) ${line[4]}`);
    L.push(
      `G) ${th.map((s) => ((s.latest!.coveragePct ?? 100) < 10 ? "DRY" : CONTAMINANT[s.latest!.contaminant!].rcr)).join("/")}`,
    );
    L.push("H) NR");
  }
  L.push("");
  L.push("SITUATIONAL AWARENESS SECTION");
  const sand = ready.filter((r) => thirdsOf(states, r.zone).some((s) => s.latest!.checks.sand === "found"));
  for (const r of sand) L.push(`K) RWY ${r.from} LOOSE SAND`);
  const twy = states.filter((s) => s.area.kind === "taxiway" && (s.verdict === "unsafe" || s.verdict === "restricted"));
  if (twy.length)
    L.push(
      `P) ${twy.map((s) => `${s.area.zone.replace("TWY-", "TWY ")} ${s.hazards.map((k) => CHECK_LABEL[k].toUpperCase()).join(", ")}`).join(". ")}`,
    );
  const apr = states.filter((s) => s.area.kind === "apron" && (s.verdict === "unsafe" || s.verdict === "restricted"));
  if (apr.length)
    L.push(
      `R) ${apr.map((s) => `${s.area.name.toUpperCase()} ${s.hazards.map((k) => CHECK_LABEL[k].toUpperCase()).join(", ")}`).join(". ")}`,
    );
  const remarks: string[] = [];
  const trig = TRIGGERS.find((x) => x.key === f.insp.trigger)!.label.toUpperCase();
  remarks.push(`URGENT INSPECTION AFTER ${trig}.`);
  for (const r of ready)
    for (const s of thirdsOf(states, r.zone))
      if (s.latest!.downgradeTo != null)
        remarks.push(
          `RWY ${r.from} ${THIRD_LABEL[s.area.third!].toUpperCase()} RWYCC DOWNGRADED ON OBSERVATION: ${s.latest!.downgradeWhy ?? ""}.`,
        );
  for (const r of ready)
    for (const s of thirdsOf(states, r.zone))
      if (s.hazards.length)
        remarks.push(
          `RWY ${r.from} ${THIRD_LABEL[s.area.third!].toUpperCase()}: ${s.hazards
            .map(
              (k) =>
                `${CHECK_LABEL[k].toUpperCase()}${s.latest!.notes?.[k] ? ` — ${s.latest!.notes[k]!.toUpperCase()}` : ""}`,
            )
            .join("; ")}.`,
        );
  for (const r of missing) remarks.push(`RWY ${r.designator} NOT YET ASSESSED.`);
  L.push(`T) ${remarks.join(" ")}`);
  return L.join("\n");
}

/* ── FAA FICON NOTAM ──────────────────────────────────────────────────── */

export function ficon(f: FormInput): string {
  const areas = areasOf(f.area);
  const states = statesOf(f.insp, areas);
  const { ready, missing } = runwaysReady(states, f.area);
  const L: string[] = ["DRAFT FIELD CONDITION NOTAMS — entered in the NOTAM system by the airport operator", ""];
  if (!ready.length) L.push("NO RUNWAY FULLY ASSESSED — NOTHING TO REPORT YET");
  for (const r of ready) {
    const th = thirdsOf(states, r.zone);
    const codes = th.map((s) => s.rwycc).join("/");
    const parts = th.map((s) => {
      const c = s.latest!;
      if (c.contaminant === "dry" || (c.coveragePct ?? 100) < 10) return "DRY";
      const pct = Math.min(100, Math.max(10, Math.round((c.coveragePct ?? 100) / 10) * 10));
      const depth = CONTAMINANT[c.contaminant!].depth && c.depthMm ? ` ${inches(c.depthMm)}` : "";
      return `${pct} PCT${depth} ${FAA_TERM[c.contaminant!]}`;
    });
    L.push(
      `!${f.site} RWY ${r.from} FICON ${codes} ${parts.join(", ")} OBS AT ${yymmddhhmm(assessedAt(states, r.zone))}`,
    );
  }
  const twy = states.filter((s) => s.area.kind === "taxiway" && (s.verdict === "unsafe" || s.verdict === "restricted"));
  for (const s of twy)
    L.push(
      `!${f.site} TWY ${s.area.zone.replace("TWY-", "")} ${s.hazards.map((k) => CHECK_LABEL[k].toUpperCase()).join(", ")}`,
    );
  const apr = states.filter((s) => s.area.kind === "apron" && (s.verdict === "unsafe" || s.verdict === "restricted"));
  for (const s of apr)
    L.push(
      `!${f.site} APRON ${s.area.name.toUpperCase()} ${s.hazards.map((k) => CHECK_LABEL[k].toUpperCase()).join(", ")}`,
    );
  for (const r of missing) L.push(`(RWY ${r.designator} not yet assessed — no NOTAM drafted)`);
  return L.join("\n");
}

/* ── FAA Part 139 self-inspection record ──────────────────────────────── */

export function part139(f: FormInput): string {
  const areas = areasOf(f.area);
  const states = statesOf(f.insp, areas);
  const trig = TRIGGERS.find((x) => x.key === f.insp.trigger)!.label;
  const L: string[] = [];
  L.push("AIRPORT SELF-INSPECTION RECORD — SPECIAL INSPECTION");
  L.push(`Airport: ${f.site}`);
  L.push(`Reason: unusual condition — ${trig}${f.insp.triggerNote ? ` (${f.insp.triggerNote})` : ""}`);
  L.push(`Started: ${utc(f.insp.openedAt)} by ${f.insp.openedBy}`);
  L.push(f.insp.closed ? `Completed: ${utc(f.insp.closed.at)} by ${f.insp.closed.by}` : "Completed: IN PROGRESS");
  L.push("");
  L.push("AREA                                   RESULT                    INSPECTED");
  for (const s of states)
    L.push(
      `${where(s).padEnd(38)} ${(VERDICT_LABEL[s.verdict] + (s.rwycc != null ? ` RWYCC ${s.rwycc}` : "")).padEnd(25)} ${s.latest ? `${utc(s.latest.at)} ${s.latest.by}` : "—"}`,
    );
  L.push("");
  L.push("DISCREPANCIES AND ACTION TAKEN");
  let n = 0;
  for (const s of states) for (const line of findingsOf(s)) L.push(`${++n}. ${where(s)}: ${line}`);
  if (!n) L.push("None.");
  const wos = f.insp.log.filter((e) => e.what.startsWith("Work order"));
  if (wos.length) {
    L.push("");
    L.push("WORK ORDERS RAISED");
    for (const e of wos) L.push(`- ${e.vars?.number} for ${e.vars?.area} (${utc(e.at)}, ${e.by})`);
  }
  L.push("");
  L.push("NOTAMS: see the field condition NOTAMs drafted from this inspection.");
  L.push(
    f.insp.closed
      ? `OUTCOME: ${f.insp.closed.outcome === "safe" ? "safe operations confirmed" : `closed with restrictions — ${f.insp.closed.restricted.join(", ")}`}${f.insp.closed.note ? ` (${f.insp.closed.note})` : ""}`
      : "OUTCOME: inspection still open — operations not confirmed",
  );
  L.push("");
  L.push("Inspector: ______________________   Signature: ______________   Date/time: ______________");
  return L.join("\n");
}

/* ── the inspection report ────────────────────────────────────────────── */

export function report(f: FormInput): string {
  const areas = areasOf(f.area);
  const states = statesOf(f.insp, areas);
  const trig = TRIGGERS.find((x) => x.key === f.insp.trigger)!.label;
  const L: string[] = [];
  L.push(`URGENT INSPECTION REPORT — ${f.site}`);
  L.push(`Produced ${utc(f.now.toISOString())}`);
  L.push("");
  L.push(`Trigger: ${trig}${f.insp.triggerNote ? ` — ${f.insp.triggerNote}` : ""}`);
  L.push(`Opened: ${utc(f.insp.openedAt)} by ${f.insp.openedBy}`);
  L.push(
    f.insp.closed
      ? `Closed: ${utc(f.insp.closed.at)} by ${f.insp.closed.by} — ${f.insp.closed.outcome === "safe" ? "SAFE OPERATIONS CONFIRMED" : "CLOSED WITH RESTRICTIONS"}`
      : "Status: IN PROGRESS — operations not confirmed",
  );
  if (f.insp.closed?.note) L.push(`Note: ${f.insp.closed.note}`);
  if (f.insp.closed?.restricted.length) L.push(`Restricted: ${f.insp.closed.restricted.join(", ")}`);
  L.push("");
  L.push("RUNWAYS (thirds from the lower-designator threshold)");
  for (const r of f.area.runways) {
    L.push(`RWY ${r.designator}: ${rcrLine(states, r) ?? "not every third assessed"}`);
    for (const s of thirdsOf(states, r.zone)) {
      const c = s.latest;
      L.push(
        `  ${THIRD_LABEL[s.area.third!]}: ${VERDICT_LABEL[s.verdict]}` +
          (c?.contaminant
            ? ` — ${CONTAMINANT[c.contaminant].label}` +
              (c.contaminant !== "dry"
                ? `, ${c.coveragePct ?? 100}% (reported ${coverageBand(c.coveragePct ?? 100) ?? "NR"})`
                : "") +
              (CONTAMINANT[c.contaminant].depth ? `, ${c.depthMm ?? "?"} mm` : "") +
              `, RWYCC ${s.rwycc}${s.matrix !== s.rwycc ? ` (matrix ${s.matrix}, lowered: ${c.downgradeWhy ?? ""})` : ""}`
            : ""),
      );
      for (const x of findingsOf(s)) L.push(`    - ${x}`);
    }
  }
  for (const kind of ["taxiway", "apron"] as const) {
    L.push("");
    L.push(kind === "taxiway" ? "TAXIWAYS" : "APRONS");
    for (const s of states.filter((x) => x.area.kind === kind)) {
      L.push(
        `  ${s.area.name}: ${VERDICT_LABEL[s.verdict]}${s.latest ? ` (${utc(s.latest.at)}, ${s.latest.by})` : ""}`,
      );
      for (const x of findingsOf(s)) L.push(`    - ${x}`);
    }
  }
  L.push("");
  L.push("LOG");
  for (const e of f.insp.log) {
    const vars = e.vars ?? {};
    const text = e.what.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ""));
    L.push(`  ${utc(e.at)} ${e.by}: ${text}`);
  }
  L.push("");
  L.push(
    "Prepared from the platform's inspection record. Runway condition codes follow the ICAO assessment matrix and are a working aid; the issued report is the SNOWTAM.",
  );
  return L.join("\n");
}

export function formText(key: FormKey, f: FormInput): string {
  return key === "snowtam" ? snowtam(f) : key === "ficon" ? ficon(f) : key === "part139" ? part139(f) : report(f);
}

/** Whether a form has anything to report: the SNOWTAM and NOTAM need a runway fully assessed. */
export function formReady(key: FormKey, f: FormInput): boolean {
  if (key === "report" || key === "part139") return true;
  const states = statesOf(f.insp, areasOf(f.area));
  return f.area.runways.some((r) => rcrLine(states, r) != null);
}

/** A reference the recipient can quote back. */
export function referenceFor(site: string, insp: UrgentInspection, at: string): string {
  const n = (insp.submissions?.length ?? 0) + 1;
  return `UI-${site}-${at.slice(0, 10).replace(/-/g, "")}-${p2(n)}`;
}

/** Append a submission and its log line; a saved copy is never edited. */
export function recordSubmission(
  insp: UrgentInspection,
  s: Omit<Submission, "id" | "ref">,
  site: string,
  newId: () => string,
): UrgentInspection {
  const ref = referenceFor(site, insp, s.at);
  const sub: Submission = { ...s, id: newId(), ref };
  return {
    ...insp,
    submissions: [...(insp.submissions ?? []), sub],
    log: [
      ...insp.log,
      {
        at: s.at,
        by: s.by,
        what: "{form} submitted to {to}, reference {ref}",
        vars: { form: s.form, to: s.to, ref },
      },
    ],
  };
}

export async function sha256(text: string): Promise<string | undefined> {
  try {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return undefined;
  }
}
